"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useTeacherApi = void 0;
var _react = require("react");
var _hooks = require("../../../hooks");
var _http = require("../../../utilities/http");
var _urls = require("../../../utilities/urls");
var _CetComponentsProvider = require("../../../providers/CetComponentsProvider");
var _EnvironmentManager = require("../EnvironmentManager.provider");
const useTeacherApi = function () {
  let isLmsApi = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  const {
    language
  } = (0, _react.useContext)(_CetComponentsProvider.CetComponentsContext);
  const {
    setPreloaderMode,
    setSchoolTeachers,
    setUserGroups,
    setUserEnvironments
  } = (0, _react.useContext)(_EnvironmentManager.EnvironmentManagerContext);
  const {
    isSessionDataReady,
    userId,
    role
  } = (0, _hooks.useSessionData)();

  // Convert the given date object into ebag string format: "MM/DD/YYYY, hh:mm:ss AM/PM"
  const convertToEbagDateString = date => {
    const options = {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true // Ensures 12-hour format with AM/PM
    };
    return date ? new Intl.DateTimeFormat('en-US', options).format(date) : undefined;
  };

  // Get all teachers of the given school (remove duplicted tecahers if any)
  const getSchoolTeachers = schoolId => {
    return _http.http.get(`${_urls.ofakimApiUrl}/lmscp/getSchoolTeachers?schoolId=${schoolId}`).then(schoolTeachers => {
      return !schoolTeachers || schoolTeachers.length === 0 ? [] : schoolTeachers.filter((teacher, index) => schoolTeachers.findIndex(t => t.user.id === teacher.user.id) === index);
    }).catch(error => {
      return null;
    });
  };

  // Get all groups of the current user in the given school
  const getUserGroups = schoolId => {
    return _http.http.get(`${_urls.ofakimApiUrl}/lmscp/getUserGroups?schoolId=${schoolId}`).then(response => {
      if (response.ok && response.value) {
        return response.value;
      }
      return null;
    }).catch(error => {
      return null;
    });
  };

  // Get all users's environments of the given product and school
  const getUserEnvironments = function (productId, schoolId, userId) {
    let isLmsApi = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;
    return isLmsApi ? _http.http.get(`${_urls.apiGatewayUrl}/LmsApi/Courses/GetUserCourses/content/${productId}/user/${userId}`).then(userEnvironments => {
      if (userEnvironments) {
        return userEnvironments.map(userEnvironment => {
          return {
            Ids: {
              Id: userEnvironment.courseId,
              AudienceID: ''
            },
            Title: userEnvironment.name,
            Dates: {
              Start: userEnvironment.startDateTime,
              End: userEnvironment.endDateTime
            },
            SchoolID: userEnvironment.instituteId,
            ProductInfo: {
              ProductId: userEnvironment.contentReference
              //TopicId: userEnvironment.TopicId
            },
            IsArchive: new Date(userEnvironment.endDateTime).getTime() < Date.now()
            //ScoreType: userAudience.ScoreType
          };
        });
      }
      return null;
    }).catch(error => {
      return null;
    }) : _http.http.get(`${_urls.ofakimApiUrl}/lmscp/getUserAudiences?productId=${productId}&schoolId=${schoolId}&userID=${userId}`).then(response => {
      if (response.ok && response.value) {
        const userAudiences = response.value;
        return userAudiences.map(userAudience => {
          return {
            Ids: {
              Id: userAudience.Id,
              AudienceID: userAudience.AudienceId
            },
            Title: userAudience.Title,
            Dates: {
              Start: userAudience.From,
              End: userAudience.To
            },
            OwnerId: userAudience.OwnerId,
            SchoolID: userAudience.SchoolId,
            ProductInfo: {
              ProductId: userAudience.ProductId,
              TopicId: userAudience.TopicId,
              TreeId: userAudience.TreeId
            },
            IsArchive: Boolean(userAudience.ArchiveYear > 0),
            ScoreType: userAudience.ScoreType
          };
        });
      }
      return null;
    }).catch(error => {
      return null;
    });
  };

  // init teacher data: fetch all relevant data from API and update context
  const initTeacherData = (0, _react.useCallback)((productId, schoolId, showPreloaderTillComplete) => {
    if (isSessionDataReady && userId && role) {
      showPreloaderTillComplete && setPreloaderMode('over');
      Promise.all([getSchoolTeachers(schoolId), getUserGroups(schoolId), getUserEnvironments(productId, schoolId, role !== 'admin' ? userId : '', isLmsApi)]).then(resData => {
        setSchoolTeachers(resData?.[0] ?? []);
        setUserGroups(resData?.[1] ?? []);
        setUserEnvironments(resData?.[2] ?? []);
        showPreloaderTillComplete && setPreloaderMode(undefined);
      });
    } else {
      setSchoolTeachers([]);
      setUserGroups([]);
      setUserEnvironments([]);
    }
  }, [isLmsApi, isSessionDataReady, userId, role, setPreloaderMode, setSchoolTeachers, setUserGroups, setUserEnvironments]);

  // Get environment audience members (teachers + groups + single students)
  const getAudienceMembers = (0, _react.useCallback)(async environmentIds => {
    let audienceMembers = null;
    if (isLmsApi && environmentIds?.Id) {
      await Promise.all([_http.http.get(`${_urls.apiGatewayUrl}/LmsApi/Courses/GetTeacherAudience/${environmentIds.Id}`).then(response => {
        return response;
      }).catch(error => null), _http.http.get(`${_urls.apiGatewayUrl}/LmsApi/Courses/GetStudentAudience/${environmentIds.Id}`).then(response => {
        return response;
      }).catch(error => null)]).then(resData => {
        audienceMembers = {
          teachers: resData?.[0]?.users?.map(teacher => teacher?.userId) ?? [],
          groups: resData?.[1]?.groups?.map(group => {
            return {
              id: group?.groupId
            };
          }) ?? [],
          singleUsers: resData?.[1]?.users.map(user => user?.userId) ?? []
        };
      });
    } else if (environmentIds?.AudienceID) {
      await _http.http.get(`${_urls.ofakimApiUrl}/lmscp/getAudienceMembers?audienceId=${environmentIds.AudienceID}`).then(response => {
        audienceMembers = response;
      }).catch(error => null);
    }
    return audienceMembers;
  }, [isLmsApi]);

  // Save/Update Learning site enviroment (update when "courseId" is not empty)
  const saveCourseEnviroment = (0, _react.useCallback)((dataToSave, onSuccess, onError) => {
    const newDataToSave = {
      name: dataToSave.Title,
      courseId: dataToSave.Ids?.Id || null,
      language: dataToSave.ProductInfo.Languege || language,
      contentReference: dataToSave.ProductInfo.ProductId,
      startDateTime: dataToSave.Dates.Start?.toISOString(),
      endDateTime: dataToSave.Dates.End?.toISOString(),
      instituteId: dataToSave.SchoolID,
      //instructions: '',
      teacherLmsAudience: {
        users: dataToSave.Teachers?.map(teacher => {
          return {
            userId: teacher.UserID
          };
        }) || []
      },
      studentLmsAudience: {
        groups: dataToSave.Audience.Groups?.map(group => {
          return {
            groupId: group
          };
        }) || [],
        users: dataToSave.Audience.SingleStudents?.map(student => {
          return {
            userId: student.UserID
          };
        }) || []
      }
    };
    _http.http.post(`${_urls.apiGatewayUrl}/LmsApi/Courses/Save`, newDataToSave).then(response => {
      if (response?.courseId) {
        onSuccess?.(response.courseId, '');
      } else {
        onError?.('error: invalid response');
      }
    }).catch(error => {
      onError?.(error);
    });
  }, [language]);
  const updateEnviroment = (0, _react.useCallback)((dataToUpdate, onSuccess, onError) => {
    if (isLmsApi) {
      saveCourseEnviroment(dataToUpdate, onSuccess, onError);
    } else {
      const newDataToSave = {
        Id: dataToUpdate.Ids.Id,
        AudienceId: dataToUpdate.Ids.AudienceID,
        userRole: role,
        startDateTime: convertToEbagDateString(dataToUpdate.Dates.Start),
        endDateTime: convertToEbagDateString(dataToUpdate.Dates.End),
        SchoolID: dataToUpdate.SchoolID,
        Languege: dataToUpdate.ProductInfo.Languege || language,
        ProductName: dataToUpdate.ProductInfo.ProductName,
        TopicId: dataToUpdate.ProductInfo.TopicId,
        ProductId: dataToUpdate.ProductInfo.ProductId,
        TreeId: dataToUpdate.ProductInfo.TreeId,
        OwnerId: dataToUpdate.OwnerId,
        archiveYear: null,
        //todo
        ScoreType: dataToUpdate.ScoreType,
        Title: dataToUpdate.Title,
        Teachers: dataToUpdate.Teachers,
        Groups: dataToUpdate.Audience.Groups || [],
        SingleUsers: dataToUpdate.Audience.SingleStudents || [],
        From: convertToEbagDateString(dataToUpdate.Dates.Start),
        //duplicate be remove
        To: convertToEbagDateString(dataToUpdate.Dates.End) //duplicate be remove
      };
      _http.http.post(`${_urls.ofakimApiUrl}/lmscp/updateCourse`, newDataToSave).then(() => {
        onSuccess?.();
      }).catch(error => {
        onError?.(error);
      });
    }
  }, [language, role, isLmsApi, saveCourseEnviroment]);
  const deleteEnviroment = (0, _react.useCallback)(async enviroment => {
    let isEnvDeleted = false;
    if (isLmsApi) {
      await _http.http.delete(`${_urls.apiGatewayUrl}/LmsApi/Courses/Delete/${enviroment.Ids.Id}`).then(response => {
        isEnvDeleted = response;
      }).catch(error => {});
    } else {
      let newDataToSave = {
        startDateTime: enviroment.Dates.Start,
        endDateTime: enviroment.Dates.End,
        SchoolID: enviroment.SchoolID,
        Languege: enviroment.ProductInfo.Languege ?? language,
        ProductName: enviroment.ProductInfo.ProductName,
        TopicId: enviroment.ProductInfo.TopicId,
        ProductId: enviroment.ProductInfo.ProductId,
        TreeId: enviroment.ProductInfo.TreeId,
        OwnerId: enviroment.OwnerId,
        AudienceId: enviroment.Ids.AudienceID,
        Id: enviroment.Ids.Id,
        ScoreType: enviroment.ScoreType
      };
      await _http.http.post(`${_urls.ofakimApiUrl}/lmscp/deleteCourse`, newDataToSave).then(response => {
        isEnvDeleted = response;
      }).catch(error => {});
    }
    return isEnvDeleted;
  }, [language, isLmsApi]);
  const saveEnviroment = (0, _react.useCallback)((dataToSave, onSuccess, onError) => {
    if (isLmsApi) {
      saveCourseEnviroment(dataToSave, onSuccess, onError);
    } else {
      let newDataToSave = {
        userRole: role,
        startDateTime: convertToEbagDateString(dataToSave.Dates.Start),
        endDateTime: convertToEbagDateString(dataToSave.Dates.End),
        SchoolID: dataToSave.SchoolID,
        Languege: dataToSave.ProductInfo.Languege || language,
        ProductName: dataToSave.ProductInfo.ProductName,
        TopicId: dataToSave.ProductInfo.TopicId,
        ProductId: dataToSave.ProductInfo.ProductId,
        TreeId: dataToSave.ProductInfo.TreeId,
        OwnerId: dataToSave.OwnerId,
        ScoreType: dataToSave.ScoreType,
        Title: dataToSave.Title,
        Teachers: dataToSave.Teachers,
        Groups: dataToSave.Audience.Groups || [],
        SingleUsers: dataToSave.Audience.SingleStudents || [],
        From: convertToEbagDateString(dataToSave.Dates.Start),
        //may be remove
        To: convertToEbagDateString(dataToSave.Dates.End) //may be remove
      };
      _http.http.post(`${_urls.ofakimApiUrl}/lmscp/saveCourse`, newDataToSave).then(response => {
        if (response?.Id && response?.AudienceId) {
          onSuccess?.(response.Id, response?.AudienceId);
        } else {
          onError?.('error: invalid response');
        }
      }).catch(error => {
        onError?.(error);
      });
    }
  }, [role, language, isLmsApi, saveCourseEnviroment]);
  return {
    initTeacherData,
    getAudienceMembers,
    updateEnviroment,
    saveEnviroment,
    deleteEnviroment
  };
};
exports.useTeacherApi = useTeacherApi;